import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { suvSignService, SuvSignConfig, SuvSignTemplate } from '@/services/suvSignService';
import { toast } from 'sonner';

export function useSuvSign() {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<SuvSignConfig | null>(null);
  const [templates, setTemplates] = useState<SuvSignTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('suvsign_config')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      setConfig(data);
      return data;
    } catch (error) {
      console.error('Error loading config:', error);
      return null;
    }
  };

  const saveConfig = async (configData: {
    api_url: string;
    api_key: string;
    webhook_secret: string;
    is_active: boolean;
  }) => {
    setLoading(true);
    try {
      // Check if config exists
      const { data: existing } = await supabase
        .from('suvsign_config')
        .select('id')
        .single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('suvsign_config')
          .update(configData)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('suvsign_config')
          .insert(configData);

        if (error) throw error;
      }

      toast.success('SuvSign configuration saved successfully');
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (testConfig?: { api_url: string; api_key: string }) => {
    setLoading(true);
    try {
      let success: boolean;
      
      if (testConfig) {
        // Test with provided config values (before saving)
        success = await suvSignService.testConnectionWithConfig(testConfig);
      } else {
        // Test with saved config
        success = await suvSignService.testConnection();
      }
      
      if (success) {
        toast.success('Connection to SuvSign API successful!');
      } else {
        toast.error('Failed to connect to SuvSign API');
      }
      return success;
    } catch (error) {
      console.error('Connection test error:', error);
      toast.error(error instanceof Error ? error.message : 'Connection test failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendToSuvSign = async (params: {
    contractId: string;
    dealId: string;
    title: string;
    fileUrl: string;
    recipientEmail: string;
    recipientName: string;
  }) => {
    setLoading(true);
    try {
      // Create document in SuvSign with signatories
      const document = await suvSignService.createDocument({
        title: params.title,
        description: `Contract for deal ${params.dealId}`,
        file_url: params.fileUrl,
        signatories: [
          { name: params.recipientName, email: params.recipientEmail },
          { name: 'Divus Legal Group', email: 'contato@divuslegalgroup.com' },
        ],
      });

      console.log('📄 Document created in SuvSign:', document.id);

      // Update local contract with SuvSign document ID
      const { error: updateError } = await supabase
        .from('deal_contracts')
        .update({
          suvsign_document_id: document.id,
          integration_provider: 'suvsign',
          status: 'draft',
          updated_at: new Date().toISOString(),
        })
        .eq('id', params.contractId);

      if (updateError) throw updateError;

      // Send for signature
      await suvSignService.sendForSignature(document.id);

      console.log('✉️ Document sent for signature');

      // Update contract status to sent
      const { error: sentError } = await supabase
        .from('deal_contracts')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', params.contractId);

      if (sentError) throw sentError;

      toast.success('Contract sent successfully via SuvSign!');
      return { success: true, document };
    } catch (error) {
      console.error('Error sending to SuvSign:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send contract via SuvSign');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const data = await suvSignService.listTemplates();
      setTemplates(data || []);
      return data;
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load SuvSign templates');
      return [];
    } finally {
      setLoadingTemplates(false);
    }
  };

  return {
    config,
    loading,
    loadConfig,
    saveConfig,
    testConnection,
    sendToSuvSign,
    templates,
    loadingTemplates,
    loadTemplates,
  };
}
